import React from 'react';
import { MessageCircle } from 'lucide-react';
import { log } from '../../lib/logger';

/**
 * Programmatically open the Crisp chatbox. Wrapped in a try/catch so a
 * mis-configured Crisp env var (or an offline visitor with the script
 * blocked) never breaks the click — it just no-ops with a friendly
 * console hint for the operator.
 */
const openChat = () => {
  try {
    if (typeof window !== 'undefined' && Array.isArray(window.$crisp)) {
      window.$crisp.push(['do', 'chat:open']);
      return;
    }
    log.info('[QuoteFooter] Crisp not initialised — Need help? click ignored.');
  } catch (err) {
    log.warn('[QuoteFooter] Crisp open failed:', err?.message || err);
  }
};

/**
 * QuoteCalculator page footer — logo + contact info + services list.
 * Pure presentational. Extracted from QuoteCalculator to keep the
 * parent focused on wizard flow.
 *
 * Includes a "Need help?" CTA that opens the Crisp chatbox in-place
 * — surfaces support at the exact moment customers hit pricing
 * confusion (Foil + White Ink + custom sizing tend to be the hot
 * spots). Falls back to a no-op if Crisp isn't configured so the link
 * doesn't render broken on dev environments.
 */
export const QuoteFooter = () => (
  <footer className="bg-[#4A7C59] text-white pt-12 pb-16 mt-12">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <img
            src="/eg3-logo.png"
            alt="Evergreen Graphics Group"
            className="h-24 w-24 mb-4 rounded-full"
            style={{ objectFit: 'contain' }}
          />
          <p className="text-green-100">Professional print solutions for your business needs.</p>
        </div>
        <div>
          <h4 className="font-semibold text-lg mb-3">Contact Us</h4>
          <p className="text-green-100 mb-2">Phone: (905) 447-0257</p>
          <p className="text-green-100 mb-2">Email: info@evergreengraphicsgroup.ca</p>
          <p className="text-green-100 mb-2">90 Venture Dr #2, Scarborough, ON M1B 3L6</p>
          <button
            type="button"
            onClick={openChat}
            data-testid="footer-need-help-btn"
            className="inline-flex items-center gap-2 mt-2 text-green-100 hover:text-white underline-offset-4 hover:underline transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Need help? Chat with us</span>
          </button>
        </div>
        <div>
          <h4 className="font-semibold text-lg mb-3">Our Services</h4>
          <ul className="text-green-100 space-y-1">
            <li>• Beer Labels</li>
            <li>• Keg Collars</li>
            <li>• Packaging Solutions</li>
            <li>• Promotional Materials</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-green-600 mt-6 pt-6 text-center text-green-100">
        <p>© 2024 Evergreen Graphics Group Inc. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default QuoteFooter;
